import { z } from 'zod';
import { ConsentPurpose, DataCategory, ErasureReason, ErasureStatus, LegalBasis } from '../enums';
import { dateSchema, idSchema, zEnum } from './common';

/**
 * Consent capture. `noticeVersion` is required rather than optional: a consent
 * record that cannot say which notice was shown proves nothing, because the
 * wording is what the person actually agreed to.
 */
export const grantConsentSchema = z.object({
  purposes: z.array(zEnum(ConsentPurpose)).min(1, 'Select at least one purpose'),
  noticeVersion: z.string().trim().min(1).max(20),
});
export type GrantConsentInput = z.infer<typeof grantConsentSchema>;

export const withdrawConsentSchema = z.object({
  purposes: z.array(zEnum(ConsentPurpose)).min(1, 'Select at least one purpose'),
});
export type WithdrawConsentInput = z.infer<typeof withdrawConsentSchema>;

export const consentNoticeSchema = z.object({
  version: z
    .string()
    .trim()
    .regex(/^\d{4}\.\d{2}\.\d+$/, 'Version looks like 2026.08.1'),
  purposes: z.array(zEnum(ConsentPurpose)).min(1),
  titleEn: z.string().trim().min(3).max(200),
  bodyEn: z.string().trim().min(20),
  titleTa: z.string().trim().max(200).optional(),
  bodyTa: z.string().trim().optional(),
  effectiveFrom: dateSchema,
});
export type ConsentNoticeInput = z.infer<typeof consentNoticeSchema>;

export const retentionRuleSchema = z.object({
  category: zEnum(DataCategory),
  /** Capped at 25 years — beyond that someone has typed a wrong number. */
  retainForDays: z.coerce.number().int().min(1).max(9125),
  basis: z.string().trim().min(10).max(300),
  isActive: z.boolean().default(true),
});
export type RetentionRuleInput = z.infer<typeof retentionRuleSchema>;

export const placeLegalHoldSchema = z.object({
  subjectType: z.string().trim().min(3).max(40).default('SeekerProfile'),
  subjectId: idSchema,
  category: zEnum(DataCategory),
  basis: zEnum(LegalBasis),
  reason: z.string().trim().min(10).max(500),
  liftsOn: dateSchema,
});
export type PlaceLegalHoldInput = z.infer<typeof placeLegalHoldSchema>;

export const requestErasureSchema = z.object({
  subjectType: z.string().trim().min(3).max(40).default('SeekerProfile'),
  subjectId: idSchema,
  reason: zEnum(ErasureReason),
});
export type RequestErasureInput = z.infer<typeof requestErasureSchema>;

/** What one category's erasure actually did. */
export interface CategoryOutcome {
  category: DataCategory;
  result: 'erased' | 'held' | 'not_present';
  /** Rows or files affected when erased. */
  affected?: number;
  /** Present only when held back. */
  basis?: LegalBasis;
  liftsOn?: string;
}

/**
 * What a deletion request would do, per category, if it were actioned today.
 *
 * This exists so the candidate sees the truth before they confirm rather than
 * after. "Delete my account" on a staffing platform can never mean "every trace
 * of you is gone" — EPF, ESI, Contract Labour and income-tax records outlive
 * the account by years, and a button that implies otherwise is a lie the person
 * only discovers when they ask for a payslip.
 */
export interface ErasurePreviewLine {
  category: DataCategory;
  /**
   * `erase` — destroyed when this request is actioned.
   * `hold`  — a statute currently forbids erasing it; `basis` names which.
   * `retain` — kept as a business record under the retention policy, not
   *            destroyed by this request.
   */
  outcome: 'erase' | 'hold' | 'retain';
  basis?: LegalBasis;
  /** ISO date the hold expires, when one applies. */
  liftsOn?: string;
}

/** A candidate's own view of their deletion request. */
export interface MyErasureRequest {
  /** Null until they have asked for anything. */
  request: {
    id: string;
    status: ErasureStatus;
    reason: ErasureReason;
    requestedAt: string;
    completedAt: string | null;
    outcome: CategoryOutcome[] | null;
  } | null;
  preview: ErasurePreviewLine[];
}
